import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Loader2, Star, Crown, Check, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PLANS = {
  seekers: {
    id: 'seekers',
    name: 'The Seekers Path',
    price: 9.99,
    interval: 'month',
    description: 'Perfect for those beginning their ancestral journey',
    features: [
      'AI Genealogy Analysis',
      'Tribal Rights Education',
      'Access to Tribal Table (Recipes)',
      'Access to Tribal Vault (Treaties)',
      'Cosmic Codes (Numerology & Astrology)',
      'Ancestral Healing Wisdom'
    ],
    icon: Star,
    color: 'blue',
    stripePriceId: process.env.REACT_APP_STRIPE_SEEKERS_PRICE_ID
  },
  crown: {
    id: 'crown',
    name: 'The Crown',
    price: 129.99,
    upfrontFee: 129.99,
    monthlyFee: 9.99,
    interval: 'month',
    description: 'Complete sovereignty package with official documentation',
    features: [
      'Everything in Seekers Path',
      'Official Genealogy Documentation',
      'Tribal Identification Card',
      'Land Rights Consultation',
      'Priority Support',
      'Advanced Legal Resources',
      'Exclusive Crown Member Events'
    ],
    icon: Crown,
    color: 'amber',
    badge: 'BEST VALUE',
    stripePriceId: process.env.REACT_APP_STRIPE_CROWN_PRICE_ID
  }
};

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { toast } = useToast();

  const [selectedPlan, setSelectedPlan] = useState('seekers');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.password !== formData.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Password Mismatch",
        description: "Passwords do not match.",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        variant: "destructive",
        title: "Invalid Password",
        description: "Password must be at least 6 characters long.",
      });
      return;
    }

    setLoading(true);

    try {
      // Step 1: Register the user
      const { confirmPassword, ...userData } = formData;
      const result = await register(userData);

      if (!result.success) {
        throw new Error(result.error || 'Registration failed');
      }

      toast({
        title: "Account Created!",
        description: "Redirecting to checkout...",
      });

      // Step 2: Create Stripe Checkout Session
      const token = localStorage.getItem('auth_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const checkoutResponse = await fetch(`${backendUrl}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription_tier: selectedPlan,
          billing_interval: 'monthly',
          origin_url: window.location.origin
        })
      });

      if (!checkoutResponse.ok) {
        const errorData = await checkoutResponse.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const checkoutData = await checkoutResponse.json();

      // Clear auth token - user must login manually after payment
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');

      // Redirect to Stripe Checkout
      window.location.href = checkoutData.checkout_url;

    } catch (error) {
      console.error('Registration error:', error);
      toast({
        variant: "destructive",
        title: "Registration Failed",
        description: error.message || "An error occurred. Please try again.",
      });
      setLoading(false);
    }
  };

  const selectedPlanData = PLANS[selectedPlan];
  const PlanIcon = selectedPlanData.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Begin Your Ancestral Journey
          </h1>
          <p className="text-lg text-gray-600">
            Create your account and choose the membership that's right for you
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Side - Account Information */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Account Information</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  required
                  placeholder="Enter your full name"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  placeholder="Create a password (min 6 characters)"
                  minLength={6}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                  placeholder="Confirm your password"
                  minLength={6}
                  disabled={loading}
                />
              </div>

              {/* Plan Selection */}
              <div className="pt-6 border-t">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Your Membership</h3>
                <div className="space-y-3">
                  {Object.values(PLANS).map((plan) => {
                    const Icon = plan.icon;
                    const isSelected = selectedPlan === plan.id;
                    const borderColor = isSelected 
                      ? plan.color === 'blue' ? 'border-blue-500' : 'border-amber-500'
                      : 'border-gray-200';
                    const bgColor = isSelected
                      ? plan.color === 'blue' ? 'bg-blue-50' : 'bg-amber-50'
                      : 'bg-white';

                    return (
                      <div
                        key={plan.id}
                        onClick={() => !loading && setSelectedPlan(plan.id)}
                        className={`border-2 ${borderColor} ${bgColor} rounded-lg p-4 cursor-pointer transition-all hover:shadow-md`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div className={`p-2 rounded-full ${plan.color === 'blue' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                              <Icon className={`w-5 h-5 ${plan.color === 'blue' ? 'text-blue-600' : 'text-amber-600'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-900">{plan.name}</h4>
                                {plan.badge && (
                                  <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-semibold">
                                    {plan.badge}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                              <div className="mt-2">
                                {plan.upfrontFee ? (
                                  <div>
                                    <span className="text-lg font-bold text-gray-900">
                                      ${plan.upfrontFee.toFixed(2)}
                                    </span>
                                    <span className="text-sm text-gray-600"> upfront + </span>
                                    <span className="text-lg font-bold text-gray-900">
                                      ${plan.monthlyFee.toFixed(2)}
                                    </span>
                                    <span className="text-sm text-gray-600">/month</span>
                                  </div>
                                ) : (
                                  <div>
                                    <span className="text-2xl font-bold text-gray-900">
                                      ${plan.price.toFixed(2)}
                                    </span>
                                    <span className="text-sm text-gray-600">/{plan.interval}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <div className={`p-1 rounded-full ${plan.color === 'blue' ? 'bg-blue-500' : 'bg-amber-500'}`}>
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-lg py-6 mt-6"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Continue to Payment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-600 mt-4">
                Already have an account?{' '}
                <a href="/login" className="text-green-600 hover:text-green-800 font-semibold" onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}>
                  Login here
                </a>
              </p>
            </form>
          </div>

          {/* Right Side - Selected Plan Details */}
          <div className="bg-white rounded-lg shadow-lg p-8 sticky top-24 h-fit">
            <div className="flex items-center gap-3 mb-6">
              <div className={`p-3 rounded-full ${selectedPlanData.color === 'blue' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                <PlanIcon className={`w-6 h-6 ${selectedPlanData.color === 'blue' ? 'text-blue-600' : 'text-amber-600'}`} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedPlanData.name}</h3>
                <p className="text-sm text-gray-600">{selectedPlanData.description}</p>
              </div>
            </div>

            <div className="mb-6 pb-6 border-b">
              {selectedPlanData.upfrontFee ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Upfront Fee</span>
                    <span className="text-2xl font-bold text-gray-900">${selectedPlanData.upfrontFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Monthly</span>
                    <span className="text-xl font-bold text-gray-900">${selectedPlanData.monthlyFee.toFixed(2)}/mo</span>
                  </div>
                  <div className="pt-2 mt-2 border-t">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Due Today</span>
                      <span className="text-3xl font-bold text-green-600">
                        ${(selectedPlanData.upfrontFee + selectedPlanData.monthlyFee).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-900 mb-1">
                    ${selectedPlanData.price.toFixed(2)}
                  </div>
                  <div className="text-gray-600">per {selectedPlanData.interval}</div>
                </div>
              )}
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
              <ul className="space-y-2">
                {selectedPlanData.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${selectedPlanData.color === 'blue' ? 'text-blue-600' : 'text-amber-600'}`} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Register;
